import { useState, useEffect, useMemo, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  CheckCircle2,
  AlertCircle,
  Cpu,
  Search,
  X,
  Loader2,
  HelpCircle,
  FolderArchive,
  FileCode,
  FolderPlus,
  Upload,
} from "lucide-react";
import { apiGet, apiUpload, getFileBlob } from "../../shared/apiClient";
import type { Impianto } from "../../shared/types";
import { useToast } from "../../hooks/useToast";
import { CollapsibleSection } from "../CollapsibleSection";

interface FileProgrammazioneItem {
  id: string;
  nomeFile: string;
  tipo: string;
  size: number;
  createdAt: string | number;
}

interface GruppoProgrammazioneItem {
  id: string;
  impiantoId: string;
  descrizione?: string;
  caricatoDaNome?: string;
  caricatoDaRuolo?: string;
  createdAt: string | number;
  totaleFile: number;
  totaleSize: number;
  files: FileProgrammazioneItem[];
}

interface ImpiantoWithCount extends Impianto {
  _count?: {
    gruppiProgrammazione?: number;
  };
}

function formatBytes(bytes: number): string {
  if (!bytes || bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
}

const isPdfOrImageFile = (filename: string): boolean => {
  const ext = filename.slice(filename.lastIndexOf('.')).toLowerCase();
  return ['.pdf', '.jpg', '.jpeg', '.png', '.gif', '.webp', '.heic', '.bmp', '.tiff'].includes(ext);
};

function parseDate(val: string | number | Date | null | undefined): Date | null {
  if (!val) return null;
  if (val instanceof Date) return isNaN(val.getTime()) ? null : val;
  if (typeof val === 'number') return new Date(val);
  if (typeof val === 'string') {
    if (/^\d+$/.test(val)) return new Date(Number(val));
    const d = new Date(val);
    return isNaN(d.getTime()) ? null : d;
  }
  return null;
}

export function ProgrammazioneImpiantiSection({
  canDownloadProg,
  canUploadProg,
}: {
  canDownloadProg: boolean;
  canUploadProg: boolean;
}) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [searchProg, setSearchProg] = useState('');
  const [debouncedSearchProg, setDebouncedSearchProg] = useState('');
  const [selectedImpiantoProg, setSelectedImpiantoProg] = useState<ImpiantoWithCount | null>(null);
  const [downloadingZipId, setDownloadingZipId] = useState<string | null>(null);

  // State per Form Upload Programmazione
  const [uploadFiles, setUploadFiles] = useState<File[]>([]);
  const [descrizioneUpload, setDescrizioneUpload] = useState('');
  const [uploadingProg, setUploadingProg] = useState(false);
  const folderInputRef = useRef<HTMLInputElement>(null);

  // Debounce ricerca impianti (300ms)
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearchProg(searchProg), 300);
    return () => clearTimeout(timer);
  }, [searchProg]);

  // Query ricerca impianti (solo impianti semaforici IS: tra tutti se canUploadProg = true, o solo quelli con programmazione se canUploadProg = false)
  const impiantiProgQuery = useQuery({
    queryKey: ['impianti-prog-search', debouncedSearchProg, canUploadProg],
    enabled: canDownloadProg && !selectedImpiantoProg && debouncedSearchProg.trim().length >= 2,
    queryFn: async () =>
      (
        await apiGet<ImpiantoWithCount[]>('/impianti', {
          search: debouncedSearchProg,
          tipo: 'IS',
          ...(canUploadProg ? {} : { hasProgrammazione: 'true' }),
        })
      ).data,
  });

  const filteredProgImpianti = useMemo(
    () => (impiantiProgQuery.data || []).filter((imp) => !imp.tipo || imp.tipo === 'IS'),
    [impiantiProgQuery.data]
  );

  // Query gruppi programmazione dell'impianto selezionato
  const gruppiProgQuery = useQuery({
    queryKey: ['impianto-programmazione', selectedImpiantoProg?.id],
    enabled: !!selectedImpiantoProg,
    queryFn: async () =>
      (await apiGet<GruppoProgrammazioneItem[]>(`/impianti/${selectedImpiantoProg!.id}/programmazione`)).data,
  });

  const gruppiProg = gruppiProgQuery.data || [];

  const validUploadFiles = useMemo(() => {
    return uploadFiles.filter((f) => !isPdfOrImageFile(f.name));
  }, [uploadFiles]);

  const excludedCount = uploadFiles.length - validUploadFiles.length;

  const handleSelectImpiantoProg = (imp: ImpiantoWithCount) => {
    setSelectedImpiantoProg(imp);
    setSearchProg('');
    setUploadFiles([]);
    setDescrizioneUpload('');
  };

  const handleClearSelectionProg = () => {
    setSelectedImpiantoProg(null);
    setSearchProg('');
    setUploadFiles([]);
    setDescrizioneUpload('');
  };

  const handleDownloadZip = async (gruppoId: string) => {
    if (!selectedImpiantoProg) return;
    setDownloadingZipId(gruppoId);
    try {
      const { url, filename } = await getFileBlob(
        `/impianti/${selectedImpiantoProg.id}/programmazione/${gruppoId}/zip`
      );
      const a = document.createElement('a');
      a.href = url;
      a.download = filename || `programmazione_${selectedImpiantoProg.codice}.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 60000);
      toast({ type: 'success', message: 'Archivio ZIP scaricato con successo' });
    } catch {
      toast({ type: 'error', message: 'Impossibile scaricare l\'archivio ZIP' });
    } finally {
      setDownloadingZipId(null);
    }
  };

  const handleUploadProgrammazione = async () => {
    if (!selectedImpiantoProg) return;
    if (validUploadFiles.length === 0) {
      toast({
        type: 'error',
        message: 'Seleziona almeno un file di programmazione valido (esclusi PDF o immagini)',
      });
      return;
    }

    setUploadingProg(true);
    try {
      const formData = new FormData();
      for (const file of validUploadFiles) {
        formData.append('file', file);
      }
      if (descrizioneUpload.trim()) {
        formData.append('descrizione', descrizioneUpload.trim());
      }

      await apiUpload(`/impianti/${selectedImpiantoProg.id}/programmazione`, formData);

      toast({
        type: 'success',
        message: `Programmazione caricata con successo (${validUploadFiles.length} file)!`,
      });

      setUploadFiles([]);
      setDescrizioneUpload('');
      if (folderInputRef.current) folderInputRef.current.value = '';

      queryClient.invalidateQueries({ queryKey: ['impianto-programmazione', selectedImpiantoProg.id] });
      queryClient.invalidateQueries({ queryKey: ['impianti-prog-search-all'] });
    } catch (err: any) {
      const errMsg = err?.response?.data?.errore || 'Upload della programmazione fallito';
      toast({ type: 'error', message: errMsg });
    } finally {
      setUploadingProg(false);
    }
  };

  return (
      <CollapsibleSection
        icon={Cpu}
        iconClass="bg-indigo-600 text-white"
        title="Programmazione Impianti"
        subtitle="Cerca un impianto, scarica i file di programmazione o carica una nuova programmazione"
        defaultOpen={false}
      >
        <div className="space-y-4 pt-1">
          {!canDownloadProg ? (
            <div className="bg-slate-50 rounded-2xl p-6 border border-slate-200 text-center space-y-2">
              <AlertCircle className="w-8 h-8 text-slate-400 mx-auto" />
              <div className="font-bold text-slate-700 text-sm">Accesso non autorizzato</div>
              <p className="text-xs text-slate-500 max-w-md mx-auto">
                La tua squadra non dispone dell'autorizzazione per accedere o scaricare i file di programmazione dei regolatori semaforici.
              </p>
            </div>
          ) : (
            <>
              {/* Sezione Selezione Impianto */}
          {selectedImpiantoProg ? (
            <div className="rounded-2xl border-2 border-indigo-500 bg-linear-to-br from-indigo-50/90 to-blue-50/50 p-4 shadow-2xs">
              <div className="flex items-start justify-between gap-3">
                <div className="space-y-1">
                  <div className="inline-flex items-center gap-1.5 text-xs font-extrabold text-indigo-700 uppercase tracking-wider">
                    <CheckCircle2 className="w-4 h-4 text-indigo-600" /> Impianto Selezionato
                  </div>
                  <div className="font-extrabold text-slate-900 text-base sm:text-lg">
                    {selectedImpiantoProg.codice} · {selectedImpiantoProg.nome}
                  </div>
                  {selectedImpiantoProg.tipo && (
                    <div className="text-xs text-indigo-800 font-medium">Tipologia: {selectedImpiantoProg.tipo}</div>
                  )}
                </div>
                <button
                  type="button"
                  className="px-3 py-1.5 text-xs bg-white flex items-center gap-1 rounded-xl shrink-0 font-bold hover:bg-slate-50 border border-slate-300 text-slate-700 shadow-2xs transition"
                  onClick={handleClearSelectionProg}
                >
                  <X className="w-3.5 h-3.5" /> Cambia Impianto
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="relative">
                <div className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none">
                  <Search className="w-4 h-4" />
                </div>
                <input
                  className="w-full pl-11 pr-11 py-2.5 text-sm font-medium text-slate-900 bg-white border border-slate-300 rounded-2xl focus:ring-2 focus:ring-indigo-500 focus:outline-hidden shadow-2xs transition"
                  value={searchProg}
                  onChange={(e) => setSearchProg(e.target.value)}
                  placeholder="Digita codice o nome impianto (es. 01001 o Via Roma)..."
                />
                {searchProg && (
                  <button
                    type="button"
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 p-1"
                    onClick={() => setSearchProg('')}
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>

              {searchProg.trim().length < 2 ? (
                <div className="rounded-xl bg-slate-50 border border-slate-200 p-3 text-xs text-slate-500 flex items-center gap-2">
                  <HelpCircle className="w-4 h-4 text-slate-400 shrink-0" />
                  <span>Digita almeno 2 caratteri per avviare la ricerca tra gli impianti.</span>
                </div>
              ) : impiantiProgQuery.isLoading ? (
                <div className="space-y-2">
                  <div className="h-14 bg-slate-100 animate-pulse rounded-2xl" />
                  <div className="h-14 bg-slate-100 animate-pulse rounded-2xl" />
                </div>
              ) : filteredProgImpianti.length === 0 ? (
                <div className="rounded-2xl bg-slate-50 border border-slate-200 p-4 text-xs sm:text-sm text-slate-600 text-center font-medium">
                  Nessun impianto trovato per "{searchProg}".
                </div>
              ) : (
                <div className="space-y-2 max-h-[280px] overflow-auto pr-1">
                  <div className="text-xs text-slate-500 font-semibold px-1">
                    Impianti trovati ({filteredProgImpianti.length}):
                  </div>
                  {filteredProgImpianti.map((imp) => {
                    const haProg = (imp._count?.gruppiProgrammazione || 0) > 0;
                    return (
                      <button
                        key={imp.id}
                        type="button"
                        className="w-full text-left rounded-2xl border border-slate-200 bg-white hover:border-indigo-500 hover:bg-indigo-50/40 active:bg-indigo-100/60 p-3.5 transition flex items-center justify-between gap-3 shadow-2xs group"
                        onClick={() => handleSelectImpiantoProg(imp)}
                      >
                        <div className="min-w-0 flex-1">
                          <div className="font-bold text-sm text-slate-900 group-hover:text-indigo-900">
                            {imp.codice} · {imp.nome}
                          </div>
                          <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                            {imp.tipo && (
                              <span className="text-xs text-slate-500">Tipologia: {imp.tipo}</span>
                            )}
                            <span
                              className={`text-[10px] font-bold px-2 py-0.5 rounded-md ${
                                haProg ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-100 text-slate-500'
                              }`}
                            >
                              {haProg ? 'Programmazione disponibile' : 'Nessuna programmazione'}
                            </span>
                          </div>
                        </div>
                        <span className="text-xs font-extrabold text-indigo-600 bg-indigo-50 group-hover:bg-indigo-100 px-2.5 py-1 rounded-xl transition shrink-0">
                          Seleziona ➔
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Dettaglio e Gestione Programmazione per Impianto Selezionato */}
          {selectedImpiantoProg && (
            <div className="space-y-4 pt-2 border-t border-slate-100">
              {/* Form Upload Nuova Programmazione (solo se ha permesso download_upload) */}
              {canUploadProg && (
                <div className="bg-slate-50 rounded-2xl p-4 border border-slate-200 space-y-3 shadow-2xs">
                  <div className="font-bold text-xs sm:text-sm text-slate-900 flex items-center gap-1.5">
                    <Upload className="w-4 h-4 text-indigo-600" />
                    <span>Carica Nuova Programmazione Regolatore</span>
                  </div>

                <div className="flex flex-wrap gap-2 items-center">
                  <label className="inline-flex items-center gap-2 bg-white hover:bg-slate-100 text-slate-800 font-extrabold text-xs px-3.5 py-2.5 rounded-xl cursor-pointer transition border border-slate-300 shadow-2xs active:scale-95">
                    <FolderPlus className="w-4 h-4 text-indigo-600" />
                    <span>📁 Seleziona Cartella Locale</span>
                    <input
                      ref={folderInputRef}
                      type="file"
                      multiple
                      // @ts-ignore
                      webkitdirectory=""
                      // @ts-ignore
                      directory=""
                      className="hidden"
                      onChange={(e) => {
                        if (e.target.files) setUploadFiles(Array.from(e.target.files));
                      }}
                    />
                  </label>
                </div>

                {uploadFiles.length > 0 && (
                  <div className="bg-white rounded-xl p-3 border border-slate-200 space-y-1 text-xs">
                    <div className="font-bold text-slate-800 flex items-center justify-between">
                      <span>
                        File validi selezionati: <strong className="text-indigo-600">{validUploadFiles.length}</strong> ({formatBytes(validUploadFiles.reduce((a, f) => a + f.size, 0))})
                      </span>
                      <button
                        type="button"
                        className="text-slate-400 hover:text-slate-600 font-bold"
                        onClick={() => setUploadFiles([])}
                      >
                        Azzera selezione
                      </button>
                    </div>

                    {excludedCount > 0 && (
                      <div className="text-amber-700 text-[11px] font-medium flex items-center gap-1">
                        <AlertCircle className="w-3.5 h-3.5 shrink-0" />
                        <span>{excludedCount} file escluso/i (i file PDF o immagini non sono caricabili nelle programmazioni)</span>
                      </div>
                    )}
                  </div>
                )}

                <input
                  className="w-full px-3.5 py-2 text-xs font-medium text-slate-900 bg-white border border-slate-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:outline-hidden"
                  placeholder="Descrizione opzionale (es. «Modifica fasatura estiva - luglio 2026»)"
                  value={descrizioneUpload}
                  onChange={(e) => setDescrizioneUpload(e.target.value)}
                />

                <button
                  type="button"
                  disabled={uploadingProg || validUploadFiles.length === 0}
                  onClick={handleUploadProgrammazione}
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white font-extrabold text-xs px-5 py-2.5 rounded-xl transition active:scale-95 disabled:opacity-50 shadow-2xs"
                >
                  {uploadingProg ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span>Caricamento in corso...</span>
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4" />
                      <span>⬆ Carica Gruppo di Programmazione ({validUploadFiles.length} file)</span>
                    </>
                  )}
                </button>
              </div>
              )}

              {/* Elenco Programmazioni Esistenti */}
              {gruppiProgQuery.isLoading ? (
                <div className="p-6 text-center text-slate-400 text-xs flex items-center justify-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin text-indigo-600" />
                  <span>Caricamento file programmazione in corso...</span>
                </div>
              ) : gruppiProg.length === 0 ? (
                <div className="bg-slate-50 rounded-2xl p-6 border border-slate-200 text-center text-slate-500 text-xs">
                  Nessun gruppo di programmazione caricato attualmente per questo impianto.
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="text-xs font-bold text-slate-700 uppercase tracking-wider px-1">
                    Programmazione salvata per questo impianto:
                  </div>

                  {gruppiProg.map((gruppo, idx) => {
                    const isDownloadingZip = downloadingZipId === gruppo.id;
                    const isPiuRecente = idx === 0;
                    const parsedDt = parseDate(gruppo.createdAt);
                    const dateStr = parsedDt
                      ? parsedDt.toLocaleDateString('it-IT', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                        })
                      : '';

                    return (
                      <div
                        key={gruppo.id}
                        className={`bg-white rounded-2xl p-4 border shadow-2xs space-y-3.5 ${
                          isPiuRecente ? 'border-emerald-400 ring-2 ring-emerald-500/10' : 'border-slate-200'
                        }`}
                      >
                        {/* Intestazione Gruppo */}
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-3 border-b border-slate-100">
                          <div className="space-y-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-extrabold text-slate-900 text-sm">
                                {gruppo.descrizione || 'Programmazione Regolatore'}
                              </span>
                              {isPiuRecente ? (
                                <span className="text-[11px] font-black px-2.5 py-0.5 rounded-full bg-emerald-100 text-emerald-800 border border-emerald-300">
                                  ★ Più recente
                                </span>
                              ) : (
                                <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">
                                  Storico #{gruppiProg.length - idx}
                                </span>
                              )}
                              <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700">
                                {gruppo.totaleFile} file ({formatBytes(gruppo.totaleSize)})
                              </span>
                            </div>
                            <div className="text-xs text-slate-500">
                              {dateStr && <span>Caricato il: <strong>{dateStr}</strong></span>}
                              {gruppo.caricatoDaNome && (
                                <span className="ml-2">
                                  | Autore: <strong>{gruppo.caricatoDaNome}</strong> ({gruppo.caricatoDaRuolo || 'operatore'})
                                </span>
                              )}
                            </div>
                          </div>

                          {/* Bottone Download ZIP */}
                          <button
                            type="button"
                            disabled={isDownloadingZip}
                            onClick={() => handleDownloadZip(gruppo.id)}
                            className="inline-flex items-center justify-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white font-extrabold text-xs px-4 py-2.5 rounded-xl transition active:scale-95 disabled:opacity-50 shadow-2xs shrink-0"
                          >
                            {isDownloadingZip ? (
                              <>
                                <Loader2 className="w-4 h-4 animate-spin" />
                                <span>Download ZIP in corso...</span>
                              </>
                            ) : (
                              <>
                                <FolderArchive className="w-4 h-4" />
                                <span>Scarica Archivio ZIP ({gruppo.totaleFile} file)</span>
                              </>
                            )}
                          </button>
                        </div>

                        {/* Elenco dei File del Gruppo (solo informativo, download disponibile solo via ZIP) */}
                        <div className="space-y-1.5">
                          <div className="text-xs font-bold text-slate-600 uppercase tracking-wider px-1">
                            File inclusi nella programmazione:
                          </div>
                          <div className="flex flex-wrap gap-1.5">
                            {gruppo.files.map((file) => (
                              <span
                                key={file.id}
                                className="inline-flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1 rounded-lg bg-slate-100 text-slate-700 border border-slate-200"
                                title={`${file.nomeFile} (${formatBytes(file.size)})`}
                              >
                                <FileCode className="w-3.5 h-3.5 text-indigo-600 shrink-0" />
                                <span>{file.nomeFile}</span>
                                <span className="text-[10px] text-slate-400">({formatBytes(file.size)})</span>
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
            </>
          )}
        </div>
      </CollapsibleSection>
  );
}
